import clsx from 'clsx';
import { LoadingSpinner, ModalPopup, Stack, Title, Tooltip } from 'components';
import { Graph } from 'modules';
import { HealthTrackerTabs } from 'modules/healthTrackerTabs';
import {
  getHealthTrackerMetric,
  setHealthTrackerMetric
} from 'modules/networkTools/localStorageTokens';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getHealthTracker,
  getHealthTrackerCategory,
  patchHealthTrackerCategory
} from 'store/healthTracker/healthTrackerActions';
import {
  resetHealthTrackerMetricList,
  updateSortsMetricList
} from 'store/healthTracker/healthTrackerSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';

import style from './healthTracker.module.scss';
import HealthTrackerForm from './healthTrackerForm';
import HealthTrackerTable from './healthTrackerTable';

const HealthTracker = () => {
  const { clientId } = useParams();
  const dispatch = useAppDispatch();
  const { enableFileUpload } = useFlags();
  const [open, setOpen] = useState(false);
  const [showSection, setShowSection] = useState(true);
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const [loadingGraph, isLoadingGraph] = useState(false);
  const [metricIdView, setMetricIdView] = useState<string | number | null>(null);
  const { healthTrackerCategoryList, healthTrackerMetricList } = useAppSelector(
    (state) => state.healthTracker
  );
  const { content, contentGraph, page, size } = healthTrackerMetricList;

  const sortByStarredAndLabel = (
    array: Array<{ name: string; primaryMetric: boolean; id: number }>
  ) => {
    return [...array].sort((a, b) => {
      if (b.primaryMetric !== a.primaryMetric) {
        return b.primaryMetric ? 1 : -1;
      }
      return a.name.localeCompare(b.name);
    });
  };

  const setPrimaryMetric = ({ metricId, type }: { metricId: string | number; type: string }) => {
    if (clientId) {
      dispatch(
        patchHealthTrackerCategory({
          clientId,
          metricId,
          type
        })
      ).then((res: any) => {
        if (!res.error) {
          setMetricIdView(metricId);
          setHealthTrackerMetric({
            metricIdView,
            url: window.location.pathname
          });
        }
      });
    }
  };

  useEffect(() => {
    if (clientId) {
      dispatch(
        getHealthTrackerCategory({
          clientId
        })
      );
    }
    return () => {
      setMetricIdView(null);
      dispatch(resetHealthTrackerMetricList());
    };
  }, []);

  useEffect(() => {
    if (clientId && metricIdView) {
      dispatch(
        getHealthTracker({
          clientId,
          metricId: metricIdView,
          page,
          size,
          sorts: null
        })
      ).then(() => {
        isLoadingGraph(false);
      });
      dispatch(updateSortsMetricList(null));
    }
  }, [clientId, metricIdView]);

  const CategoryList = useMemo(
    () => sortByStarredAndLabel(healthTrackerCategoryList.filter((a) => a.hasEntries)),
    [healthTrackerCategoryList]
  );

  useEffect(() => {
    if (CategoryList?.length > 0) {
      if (getHealthTrackerMetric().url === window.location.pathname) {
        setMetricIdView(getHealthTrackerMetric().metricIdView);
      } else {
        setMetricIdView(CategoryList[0].id);
        setHealthTrackerMetric({
          metricIdView: CategoryList[0].id,
          url: window.location.pathname
        });
      }
    }
  }, []);

  const handleNextMetric = () => {
    if (!CategoryList.length) return;
    const currentIndex = CategoryList.findIndex((item) => item.id === metricIdView);
    if (currentIndex < CategoryList.length - 1) {
      setMetricIdView(CategoryList[currentIndex + 1].id);
      setHealthTrackerMetric({
        metricIdView: CategoryList[currentIndex + 1].id,
        url: window.location.pathname
      });
      isLoadingGraph(true);
    }
  };

  const handlePrevMetric = () => {
    if (!CategoryList.length) return;
    const currentIndex = CategoryList.findIndex((item) => item.id === metricIdView);
    if (currentIndex > 0) {
      setMetricIdView(CategoryList[currentIndex - 1].id);
      setHealthTrackerMetric({
        metricIdView: CategoryList[currentIndex - 1].id,
        url: window.location.pathname
      });
      isLoadingGraph(true);
    }
  };

  const handleModalClose = () => {
    setOpen(false);
  };
  const handleToggleSection = () => setShowSection(!showSection);

  const isFirst = CategoryList.length > 0 && CategoryList[0].id === metricIdView;
  const isLast =
    CategoryList.length > 0 && CategoryList[CategoryList.length - 1].id === metricIdView;
  const selectedCategory = CategoryList.find((a) => a.id === metricIdView);

  return (
    <Stack width="100%" data-test-id="profile-snapshot">
      <Stack className={style.wrapper} flexDirection="row">
        <p className={style.title}>Health Tracker</p>
        <div>
          <span
            onClick={() => setOpen(true)}
            className={clsx('material-icons-outlined', style.icon)}
          >
            open_in_new
          </span>
          {enableFileUpload && (
            <span
              onClick={handleToggleSection}
              className={clsx('material-icons-outlined', style.icon)}
            >
              {showSection ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            </span>
          )}
        </div>
      </Stack>
      {open && (
        <ModalPopup
          open={open}
          customClassInsideContainer={clsx(
            style.modalWrapper,
            !metricIdView && style.modalWrapperEmpty
          )}
          handleClose={() => (formDirty ? setUnsavedChangesModalOpen(true) : handleModalClose())}
        >
          <Stack width="100%">
            <Title text="Health Tracker" type="h3" />
            <HealthTrackerForm
              metricIdView={metricIdView}
              setFormDirty={setFormDirty}
              unsavedChangesModalOpen={unsavedChangesModalOpen}
              setUnsavedChangesModalOpen={setUnsavedChangesModalOpen}
              handleModalClose={handleModalClose}
            />
            {healthTrackerCategoryList.filter((a) => a.hasEntries).length > 0 && (
              <HealthTrackerTabs
                list={healthTrackerCategoryList.filter((a) => a.hasEntries)}
                setPrimaryMetric={setPrimaryMetric}
                getMetricId={setMetricIdView}
              />
            )}
            {metricIdView && (
              <HealthTrackerTable
                metricId={metricIdView}
                resetMetricIdView={() => {
                  if (CategoryList.length === 0) {
                    setMetricIdView(null);
                    return;
                  }

                  const currentIndex = CategoryList.findIndex((item) => item.id === metricIdView);
                  const removedTab = CategoryList[currentIndex];
                  if (removedTab.primaryMetric && clientId) {
                    dispatch(
                      patchHealthTrackerCategory({
                        clientId,
                        metricId: removedTab.id,
                        type: !removedTab.primaryMetric
                      })
                    );
                  }

                  if (currentIndex > 0) {
                    if (currentIndex - 1 > 0) {
                      handlePrevMetric();
                    } else {
                      handleNextMetric();
                    }
                  } else {
                    setMetricIdView(null);
                    setHealthTrackerMetric({
                      metricIdView: null,
                      url: window.location.pathname
                    });
                  }
                }}
              />
            )}
          </Stack>
        </ModalPopup>
      )}
      {(enableFileUpload && !showSection) ||
        (content?.length > 0 && (
          <Stack className={style.graphWrap}>
            {CategoryList.length > 0 && metricIdView && (
              <Stack className={style.graphTitle}>
                <span
                  className={clsx('material-icons', isFirst && style.notActive)}
                  onClick={!isFirst ? handlePrevMetric : undefined}
                >
                  keyboard_arrow_down
                </span>
                {selectedCategory && selectedCategory.name.length > 26 ? (
                  <Tooltip blackTooltip toolTipText={selectedCategory?.name}>
                    <p>{selectedCategory?.name}</p>
                  </Tooltip>
                ) : (
                  <p>{selectedCategory?.name}</p>
                )}
                <span
                  className={clsx('material-icons', isLast && style.notActive)}
                  onClick={!isLast ? handleNextMetric : undefined}
                >
                  keyboard_arrow_down
                </span>
              </Stack>
            )}
            {selectedCategory &&
              (loadingGraph ? (
                <div className={clsx(style.graphIsLoading, style.graph)}>
                  <LoadingSpinner size={20} />
                </div>
              ) : (
                <Stack
                  className={clsx(
                    style.graph,
                    contentGraph.length === 1 && style.graphDisabledBtns
                  )}
                >
                  <Graph content={contentGraph} selectedCategory={selectedCategory} />
                </Stack>
              ))}
          </Stack>
        ))}
    </Stack>
  );
};

export default HealthTracker;
